import { useState, useEffect } from 'react'
import { useOpenAICalc } from '../../hooks/useCalculate'
import { useAnalytics } from '../../hooks/useAnalytics'
import { FormulaDisclosure } from '../shared/FormulaDisclosure'
import { FieldTooltip } from '../shared/FieldTooltip'
import { MobileCostBar } from '../shared/MobileCostBar'
import { ScenarioPresets } from '../shared/ScenarioPresets'
import type { OpenAIRequest } from '../../api/types'

const MODELS_STANDARD = [
  { value: 'gpt-4o',      label: 'GPT-4o (latest stable)' },
  { value: 'gpt-4o-mini', label: 'GPT-4o mini (high-volume)' },
  { value: 'o3-mini',     label: 'o3-mini (reasoning)' },
  { value: 'gpt-4',       label: 'GPT-4 (legacy 8K)' },
]

const MODELS_PTU = [
  { value: 'gpt-4o',      label: 'GPT-4o' },
  { value: 'gpt-4o-mini', label: 'GPT-4o mini' },
  { value: 'gpt-4',       label: 'GPT-4' },
]

const defaultInputs: OpenAIRequest = {
  deploymentType: 'standard',
  standard: {
    model: 'gpt-4o',
    inputTokensPerDay: 1_000_000,
    outputTokensPerDay: 200_000,
    imageRequestsPerDay: 0,
  },
  provisioned: {
    model: 'gpt-4o',
    ptuCount: 100,
    commitmentTerm: 'hourly',
    activeDaysPerMonth: 22,
  },
}

function fmt(n: number) { return `£${n.toFixed(2)}` }
function fmtN(n: number) { return n.toLocaleString('en-GB') }

export default function OpenAICalc() {
  const [inputs, setInputs] = useState<OpenAIRequest>(defaultInputs)
  const { data, isLoading } = useOpenAICalc(inputs)
  const { trackCalculatorUsed } = useAnalytics()
  useEffect(() => {
    if (data?.totalMonthly) {
      trackCalculatorUsed({ calculator: 'azure-openai', result_gbp: data.totalMonthly, tier: inputs.standard?.model ?? inputs.provisioned?.model })
    }
  }, [data?.totalMonthly])

  const visibleTotal = data?.totalMonthly ?? 0

  function setDeploymentType(type: 'standard' | 'provisioned') {
    setInputs((prev) => ({ ...prev, deploymentType: type }))
  }

  function patchStandard(patch: Partial<NonNullable<OpenAIRequest['standard']>>) {
    setInputs((prev) => ({ ...prev, standard: { ...prev.standard!, ...patch } }))
  }

  function patchProvisioned(patch: Partial<NonNullable<OpenAIRequest['provisioned']>>) {
    setInputs((prev) => ({ ...prev, provisioned: { ...prev.provisioned!, ...patch } }))
  }

  const isStandard = inputs.deploymentType === 'standard'
  const bd = data?.breakdown
  const activeDaysPerMonth = inputs.provisioned?.activeDaysPerMonth ?? 22
  const ptuCount = inputs.provisioned?.ptuCount ?? 100
  const breakEvenDay = bd ? Math.ceil(bd.breakEvenDaysPerMonth) : null
  const comparisonInputSummary = `${fmtN(inputs.standard?.inputTokensPerDay ?? 0)} input + ${fmtN(inputs.standard?.outputTokensPerDay ?? 0)} output tokens/day`
  const standardDailyCostForChart = bd ? bd.standardTotal / 30 : 0
  const chartWidth = 280
  const chartHeight = 88
  const chartPadding = 10
  const chartMaxCost = bd ? Math.max(bd.ptuMonthlyTotal, standardDailyCostForChart * 31, 1) : 1
  const toX = (day: number) => chartPadding + (day / 31) * (chartWidth - chartPadding * 2)
  const toY = (cost: number) => chartHeight - chartPadding - (cost / chartMaxCost) * (chartHeight - chartPadding * 2)

  const formulaLines = isStandard ? [
    `Input tokens: ${fmtN(inputs.standard?.inputTokensPerDay ?? 0)}/day × 30 days ÷ 1,000 × rate`,
    `Output tokens: ${fmtN(inputs.standard?.outputTokensPerDay ?? 0)}/day × 30 days ÷ 1,000 × rate`,
    `Image requests: ${fmtN(inputs.standard?.imageRequestsPerDay ?? 0)}/day × 30 days × £0.0577`,
    `Total = input cost + output cost + image cost`,
  ] : [
    `PTU hourly rate: £0.7524/PTU/hr (Provisioned Managed Global)`,
    `Commitment discount applied: ${inputs.provisioned?.commitmentTerm}`,
    `Monthly total = PTU count × effective hourly rate × 730 hrs`,
    `Note: PTU charges the full month regardless of actual usage.`,
  ]

  return (
    <div className="grid grid-cols-1 xl:grid-cols-[1fr_400px] gap-6">

      {/* ── Inputs ── */}
      <div className="space-y-6">

        <ScenarioPresets presets={[
          {
            label: 'GPT-4o-mini chatbot',
            description: 'gpt-4o-mini · 10M input / 2M output tokens/mo · Standard PAYG',
            apply: () => setInputs(prev => ({ ...prev, deploymentType: 'standard', standard: { ...prev.standard!, model: 'gpt-4o-mini', inputTokensPerDay: 333_333, outputTokensPerDay: 66_667 } })),
          },
          {
            label: 'GPT-4o assistant',
            description: 'gpt-4o · 5M input / 1M output tokens/mo · Standard PAYG',
            apply: () => setInputs(prev => ({ ...prev, deploymentType: 'standard', standard: { ...prev.standard!, model: 'gpt-4o', inputTokensPerDay: 166_667, outputTokensPerDay: 33_333 } })),
          },
          {
            label: 'PTU break-even check',
            description: 'gpt-4o · 50M input / 10M output tokens/mo · Provisioned (PTU)',
            apply: () => setInputs(prev => ({ ...prev, deploymentType: 'provisioned', provisioned: { ...prev.provisioned!, model: 'gpt-4o' } })),
          },
        ]} />

        {/* Deployment type selector */}
        <div className="rounded-xl p-5 space-y-4" style={{ backgroundColor: 'var(--surface-container)', border: '1px solid var(--outline-variant)' }}>
          <h2 className="text-[11px] font-bold uppercase tracking-widest" style={{ color: 'var(--text-tertiary)' }}>Deployment type</h2>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {(['standard', 'provisioned'] as const).map((type) => (
              <button
                key={type}
                onClick={() => setDeploymentType(type)}
                className="rounded-lg p-4 text-left transition-all"
                style={{
                  border: `2px solid ${inputs.deploymentType === type ? '#0078d4' : 'var(--outline-variant)'}`,
                  backgroundColor: inputs.deploymentType === type ? 'color-mix(in srgb, #0078d4 10%, var(--surface-container))' : 'var(--bg-surface)',
                }}
              >
                <p className="text-[13px] font-semibold" style={{ color: inputs.deploymentType === type ? '#0078d4' : 'var(--text-primary)' }}>
                  {type === 'standard' ? 'Standard (PAYG)' : 'Provisioned (PTU)'}
                </p>
                <p className="text-[11px] mt-1" style={{ color: 'var(--text-tertiary)' }}>
                  {type === 'standard' ? 'Pay per token · scales to zero' : 'Fixed hourly rate · reserved capacity'}
                </p>
              </button>
            ))}
          </div>

          {/* Always-visible PTU info */}
          <div className="rounded-lg p-3 text-[12px]" style={{ backgroundColor: 'color-mix(in srgb, #0078d4 8%, var(--bg-surface))', border: '1px solid color-mix(in srgb, #0078d4 30%, transparent)' }}>
            <p className="font-semibold mb-1" style={{ color: '#0078d4' }}>When does PTU save money?</p>
            <p style={{ color: 'var(--text-secondary)' }}>
              Provisioned Throughput Units give you reserved capacity at a fixed hourly rate.
              They only save money if you&apos;re sustaining load for approximately{' '}
              <span className="font-semibold">7+ days per month</span>.
              Below that threshold, Standard PAYG is cheaper — you only pay for tokens you actually use.
            </p>
          </div>
        </div>

        {/* Standard inputs */}
        {isStandard && (
          <div className="rounded-xl p-5 space-y-4" style={{ backgroundColor: 'var(--surface-container)', border: '1px solid var(--outline-variant)' }}>
            <h2 className="text-[11px] font-bold uppercase tracking-widest" style={{ color: 'var(--text-tertiary)' }}>Standard PAYG configuration</h2>

            <div>
              <label className="block text-[12px] font-medium mb-1.5" style={{ color: 'var(--text-secondary)' }}>Model</label>
              <select
                value={inputs.standard?.model}
                onChange={(e) => patchStandard({ model: e.target.value as NonNullable<OpenAIRequest['standard']>['model'] })}
                className="w-full rounded-lg px-3 py-2 text-[13px]"
                style={{ backgroundColor: 'var(--bg-surface)', border: '1px solid var(--outline-variant)', color: 'var(--text-primary)' }}
              >
                {MODELS_STANDARD.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
              </select>
            </div>

            <div>
              <label className="block text-[12px] font-medium mb-1" style={{ color: 'var(--text-secondary)' }}>
                Input tokens per day
              </label>
              <p className="text-[11px] mb-1.5" style={{ color: 'var(--text-tertiary)' }}>1M tokens ≈ 750,000 words ≈ a large novel</p>
              <input
                type="number"
                min={0}
                value={inputs.standard?.inputTokensPerDay}
                onChange={(e) => patchStandard({ inputTokensPerDay: Number(e.target.value) })}
                className="w-full rounded-lg px-3 py-2 text-[13px]"
                style={{ backgroundColor: 'var(--bg-surface)', border: '1px solid var(--outline-variant)', color: 'var(--text-primary)' }}
              />
            </div>

            <div>
              <label className="block text-[12px] font-medium mb-1" style={{ color: 'var(--text-secondary)' }}>Output tokens per day</label>
              <p className="text-[11px] mb-1.5" style={{ color: 'var(--text-tertiary)' }}>Output tokens cost 2–4× more than input tokens</p>
              <input
                type="number"
                min={0}
                value={inputs.standard?.outputTokensPerDay}
                onChange={(e) => patchStandard({ outputTokensPerDay: Number(e.target.value) })}
                className="w-full rounded-lg px-3 py-2 text-[13px]"
                style={{ backgroundColor: 'var(--bg-surface)', border: '1px solid var(--outline-variant)', color: 'var(--text-primary)' }}
              />
            </div>

            {(inputs.standard?.model === 'gpt-4o' || inputs.standard?.model === 'gpt-4o-mini') && (
              <div>
                <label className="block text-[12px] font-medium mb-1" style={{ color: 'var(--text-secondary)' }}>Image requests per day (vision)</label>
                <input
                  type="number"
                  min={0}
                  value={inputs.standard?.imageRequestsPerDay}
                  onChange={(e) => patchStandard({ imageRequestsPerDay: Number(e.target.value) })}
                  className="w-full rounded-lg px-3 py-2 text-[13px]"
                  style={{ backgroundColor: 'var(--bg-surface)', border: '1px solid var(--outline-variant)', color: 'var(--text-primary)' }}
                />
              </div>
            )}
          </div>
        )}

        {/* PTU inputs */}
        {!isStandard && (
          <div className="rounded-xl p-5 space-y-4" style={{ backgroundColor: 'var(--surface-container)', border: '1px solid var(--outline-variant)' }}>
            <h2 className="text-[11px] font-bold uppercase tracking-widest" style={{ color: 'var(--text-tertiary)' }}>Provisioned (PTU) configuration</h2>

            <div>
              <label className="block text-[12px] font-medium mb-1.5" style={{ color: 'var(--text-secondary)' }}>Model</label>
              <select
                value={inputs.provisioned?.model}
                onChange={(e) => patchProvisioned({ model: e.target.value as NonNullable<OpenAIRequest['provisioned']>['model'] })}
                className="w-full rounded-lg px-3 py-2 text-[13px]"
                style={{ backgroundColor: 'var(--bg-surface)', border: '1px solid var(--outline-variant)', color: 'var(--text-primary)' }}
              >
                {MODELS_PTU.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
              </select>
            </div>

            <div>
              <label className="block text-[12px] font-medium mb-1" style={{ color: 'var(--text-secondary)' }}>PTU count <FieldTooltip content="Provisioned Throughput Units — reserved capacity for consistent throughput. Billed monthly regardless of actual usage." /></label>
              <p className="text-[11px] mb-1.5" style={{ color: 'var(--text-tertiary)' }}>1 PTU ≈ 6 tokens/second sustained throughput for GPT-4o</p>
              <input
                type="number"
                min={1}
                max={10000}
                value={inputs.provisioned?.ptuCount}
                onChange={(e) => patchProvisioned({ ptuCount: Number(e.target.value) })}
                className="w-full rounded-lg px-3 py-2 text-[13px]"
                style={{ backgroundColor: 'var(--bg-surface)', border: '1px solid var(--outline-variant)', color: 'var(--text-primary)' }}
              />
            </div>

            <div>
              <label className="block text-[12px] font-medium mb-1.5" style={{ color: 'var(--text-secondary)' }}>Commitment term</label>
              <div className="flex gap-2">
                {(['hourly', '1month', '1year'] as const).map((term) => (
                  <button
                    key={term}
                    onClick={() => patchProvisioned({ commitmentTerm: term })}
                    className="flex-1 py-1.5 rounded-lg text-[12px] font-medium transition-colors"
                    style={{
                      backgroundColor: inputs.provisioned?.commitmentTerm === term ? '#0078d4' : 'var(--bg-surface)',
                      color: inputs.provisioned?.commitmentTerm === term ? '#fff' : 'var(--text-secondary)',
                      border: `1px solid ${inputs.provisioned?.commitmentTerm === term ? '#0078d4' : 'var(--outline-variant)'}`,
                    }}
                  >
                    {term === 'hourly' ? 'Hourly' : term === '1month' ? '1-month' : '1-year'}
                  </button>
                ))}
              </div>
              {inputs.provisioned?.commitmentTerm === '1year' && (
                <p className="text-[11px] mt-1.5" style={{ color: '#107c10' }}>33% discount applied on annual commitment</p>
              )}
              {inputs.provisioned?.commitmentTerm === '1month' && (
                <p className="text-[11px] mt-1.5" style={{ color: '#107c10' }}>5% discount applied on monthly commitment</p>
              )}
            </div>

            <div>
              <label className="block text-[12px] font-medium mb-1" style={{ color: 'var(--text-secondary)' }}>
                Active days per month: <span className="font-bold" style={{ color: 'var(--text-primary)' }}>{inputs.provisioned?.activeDaysPerMonth}</span>
              </label>
              <p className="text-[11px] mb-1.5" style={{ color: 'var(--text-tertiary)' }}>Used to calculate break-even vs PAYG. PTU is always billed regardless of this.</p>
              <input
                type="range"
                min={1}
                max={31}
                value={inputs.provisioned?.activeDaysPerMonth}
                onChange={(e) => patchProvisioned({ activeDaysPerMonth: Number(e.target.value) })}
                className="w-full accent-[#0078d4]"
              />
              <div className="flex justify-between text-[10px] mt-0.5" style={{ color: 'var(--text-tertiary)' }}>
                <span>1 day</span>
                <span>22 days (typical)</span>
                <span>31 days</span>
              </div>
            </div>
          </div>
        )}

        {/* Currency note */}
        {data?.currencyNote && (
          <div className="rounded-lg p-3 text-[12px]" style={{ backgroundColor: 'color-mix(in srgb, #e8b000 8%, var(--bg-surface))', border: '1px solid color-mix(in srgb, #e8b000 30%, transparent)' }}>
            <span className="mr-1">💱</span>
            <span style={{ color: 'var(--text-secondary)' }}>{data.currencyNote}</span>
          </div>
        )}
      </div>

      {/* ── Results panel ── */}
      <div className="space-y-4" id="openai-result">
        <div className="rounded-xl p-5 sticky top-4" style={{ backgroundColor: 'var(--surface-container)', border: '1px solid var(--outline-variant)' }}>
          <p className="text-[11px] font-bold uppercase tracking-widest mb-1" style={{ color: 'var(--text-tertiary)' }}>
            {isStandard ? 'Standard PAYG' : 'PTU'} monthly estimate
          </p>
          {isLoading ? (
            <p className="text-[28px] font-bold" style={{ color: 'var(--text-tertiary)' }}>Calculating…</p>
          ) : (
            <p className="text-[28px] font-bold" style={{ color: '#0078d4' }}>
              {fmt(visibleTotal)}
            </p>
          )}
          <p className="text-[11px] mt-0.5" style={{ color: 'var(--text-tertiary)' }}>UK South · GBP estimate</p>

          {data && (
            <div className="overflow-x-auto">
            <table className="w-full mt-4 text-[12px] border-collapse">
              <tbody>
                {isStandard ? (
                  <>
                    <tr style={{ borderBottom: '1px solid var(--outline-variant)' }}>
                      <td className="py-1.5 pr-2" style={{ color: 'var(--text-secondary)' }}>Input tokens</td>
                      <td className="py-1.5 text-right font-mono" style={{ color: 'var(--text-primary)' }}>{fmt(bd?.inputTokenCost ?? 0)}</td>
                    </tr>
                    <tr style={{ borderBottom: '1px solid var(--outline-variant)' }}>
                      <td className="py-1.5 pr-2" style={{ color: 'var(--text-secondary)' }}>Output tokens</td>
                      <td className="py-1.5 text-right font-mono" style={{ color: 'var(--text-primary)' }}>{fmt(bd?.outputTokenCost ?? 0)}</td>
                    </tr>
                    {(bd?.imageCost ?? 0) > 0 && (
                      <tr style={{ borderBottom: '1px solid var(--outline-variant)' }}>
                        <td className="py-1.5 pr-2" style={{ color: 'var(--text-secondary)' }}>Image requests</td>
                        <td className="py-1.5 text-right font-mono" style={{ color: 'var(--text-primary)' }}>{fmt(bd?.imageCost ?? 0)}</td>
                      </tr>
                    )}
                    <tr className="font-bold" style={{ borderTop: '2px solid var(--outline-variant)' }}>
                      <td className="py-2 pr-2" style={{ color: 'var(--text-primary)' }}>Total</td>
                      <td className="py-2 text-right font-mono" style={{ color: '#0078d4' }}>{fmt(bd?.standardTotal ?? 0)}</td>
                    </tr>
                  </>
                ) : (
                  <>
                    <tr style={{ borderBottom: '1px solid var(--outline-variant)' }}>
                      <td className="py-1.5 pr-2" style={{ color: 'var(--text-secondary)' }}>PTU hourly cost</td>
                      <td className="py-1.5 text-right font-mono" style={{ color: 'var(--text-primary)' }}>{fmt(bd?.ptuHourlyCost ?? 0)}/hr</td>
                    </tr>
                    <tr style={{ borderBottom: '1px solid var(--outline-variant)' }}>
                      <td className="py-1.5 pr-2" style={{ color: 'var(--text-secondary)' }}>730 hours × rate</td>
                      <td className="py-1.5 text-right font-mono" style={{ color: 'var(--text-primary)' }}>{fmt(bd?.ptuMonthlyTotal ?? 0)}</td>
                    </tr>
                    <tr className="font-bold" style={{ borderTop: '2px solid var(--outline-variant)' }}>
                      <td className="py-2 pr-2" style={{ color: 'var(--text-primary)' }}>Total</td>
                      <td className="py-2 text-right font-mono" style={{ color: '#0078d4' }}>{fmt(bd?.ptuMonthlyTotal ?? 0)}</td>
                    </tr>
                  </>
                )}
              </tbody>
            </table>
            </div>
          )}

          {data?.equivalentStandardCost !== null && data?.equivalentStandardCost !== undefined && (
            <div className="mt-3 p-2.5 rounded-lg text-[11px]" style={{ backgroundColor: 'var(--bg-surface)', border: '1px solid var(--outline-variant)' }}>
              <p style={{ color: 'var(--text-tertiary)' }}>
                Equivalent Standard PAYG cost at {inputs.provisioned?.activeDaysPerMonth} active days:{' '}
                <span className="font-semibold" style={{ color: 'var(--text-secondary)' }}>{fmt(data.equivalentStandardCost)}</span>
              </p>
            </div>
          )}

          {data?.equivalentPTUCost !== null && data?.equivalentPTUCost !== undefined && (
            <div className="mt-3 p-2.5 rounded-lg text-[11px]" style={{ backgroundColor: 'var(--bg-surface)', border: '1px solid var(--outline-variant)' }}>
              <p style={{ color: 'var(--text-tertiary)' }}>
                Equivalent PTU cost ({ptuCount} PTUs, hourly):{' '}
                <span className="font-semibold" style={{ color: 'var(--text-secondary)' }}>{fmt(data.equivalentPTUCost)}</span>
              </p>
            </div>
          )}

          {bd && (
            <div className="mt-4 rounded-lg p-3 space-y-3" style={{ backgroundColor: 'var(--bg-surface)', border: '1px solid var(--outline-variant)' }}>
              <p className="text-[11px] font-bold uppercase tracking-widest" style={{ color: 'var(--text-tertiary)' }}>Break-even analysis</p>
              <p className="text-[11px]" style={{ color: 'var(--text-secondary)' }}>
                At your usage level ({comparisonInputSummary}):
              </p>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                <div className="rounded-lg p-2.5" style={{ backgroundColor: 'var(--surface-container)', border: `1px solid ${isStandard ? '#0078d4' : 'var(--outline-variant)'}` }}>
                  <p className="text-[10px]" style={{ color: 'var(--text-tertiary)' }}>Standard PAYG</p>
                  <p className="text-[18px] font-bold" style={{ color: isStandard ? '#0078d4' : 'var(--text-primary)' }}>{fmt(bd.standardTotal)}</p>
                </div>
                <div className="rounded-lg p-2.5" style={{ backgroundColor: 'var(--surface-container)', border: `1px solid ${!isStandard ? '#0078d4' : 'var(--outline-variant)'}` }}>
                  <p className="text-[10px]" style={{ color: 'var(--text-tertiary)' }}>PTU ({ptuCount} units)</p>
                  <p className="text-[18px] font-bold" style={{ color: !isStandard ? '#0078d4' : 'var(--text-primary)' }}>{fmt(bd.ptuMonthlyTotal)}</p>
                </div>
              </div>

              {bd.breakEvenDaysPerMonth < 32 ? (
                <>
                  <p className="text-[12px]" style={{ color: 'var(--text-secondary)' }}>
                    PTU becomes cheaper if you sustain load for more than{' '}
                    <span className="font-semibold" style={{ color: 'var(--text-primary)' }}>{breakEvenDay} days/month</span>.
                  </p>
                  <p className="text-[12px]" style={{ color: 'var(--text-secondary)' }}>
                    You entered <span className="font-semibold" style={{ color: 'var(--text-primary)' }}>{activeDaysPerMonth} active days</span> →{' '}
                    <span className="font-semibold" style={{ color: bd.recommendation === 'standard' ? '#107c10' : bd.recommendation === 'provisioned' ? '#0078d4' : '#e8b000' }}>
                      {bd.recommendation === 'standard' ? 'Standard is cheaper' : bd.recommendation === 'provisioned' ? 'PTU is cheaper' : 'borderline'}
                    </span>
                  </p>

                  <div className="rounded-lg p-2" style={{ backgroundColor: 'var(--surface-container)', border: '1px solid var(--outline-variant)' }}>
                    <svg viewBox={`0 0 ${chartWidth} ${chartHeight}`} className="w-full h-[88px]" aria-label="Break-even chart comparing Standard PAYG and PTU costs over active days per month">
                      <line x1={toX(0)} y1={toY(0)} x2={toX(31)} y2={toY(0)} stroke="var(--outline-variant)" strokeWidth="1" />
                      <polyline
                        fill="none"
                        stroke="#107c10"
                        strokeWidth="2.5"
                        points={`${toX(0)},${toY(0)} ${toX(31)},${toY(standardDailyCostForChart * 31)}`}
                      />
                      <polyline
                        fill="none"
                        stroke="#0078d4"
                        strokeWidth="2.5"
                        points={`${toX(0)},${toY(bd.ptuMonthlyTotal)} ${toX(31)},${toY(bd.ptuMonthlyTotal)}`}
                      />
                      <line x1={toX(activeDaysPerMonth)} y1={toY(0)} x2={toX(activeDaysPerMonth)} y2={toY(chartMaxCost)} stroke="var(--outline-variant)" strokeDasharray="3 3" strokeWidth="1" />
                      {breakEvenDay !== null && (
                        <line x1={toX(Math.min(31, breakEvenDay))} y1={toY(0)} x2={toX(Math.min(31, breakEvenDay))} y2={toY(chartMaxCost)} stroke="#e8b000" strokeDasharray="4 3" strokeWidth="1.5" />
                      )}
                      <circle cx={toX(activeDaysPerMonth)} cy={toY(standardDailyCostForChart * activeDaysPerMonth)} r="3" fill="#107c10" />
                      <circle cx={toX(activeDaysPerMonth)} cy={toY(bd.ptuMonthlyTotal)} r="3" fill="#0078d4" />
                    </svg>

                    <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-[10px]" style={{ color: 'var(--text-tertiary)' }}>
                      <span><span className="inline-block w-2.5 h-2.5 rounded-full mr-1" style={{ backgroundColor: '#107c10' }} />Standard PAYG</span>
                      <span><span className="inline-block w-2.5 h-2.5 rounded-full mr-1" style={{ backgroundColor: '#0078d4' }} />PTU</span>
                      <span><span className="inline-block w-2.5 h-2.5 rounded-full mr-1" style={{ backgroundColor: '#e8b000' }} />Break-even day {breakEvenDay}</span>
                    </div>
                  </div>
                </>
              ) : (
                <p className="text-[12px]" style={{ color: 'var(--text-secondary)' }}>
                  At your usage level, Standard PAYG is cheaper for any number of active days. PTU would only save money at much higher token volumes.
                </p>
              )}

              <div
                className="rounded-lg p-3 text-[12px]"
                style={{
                  backgroundColor: bd.recommendation === 'standard'
                    ? 'color-mix(in srgb, #107c10 10%, var(--bg-surface))'
                    : bd.recommendation === 'provisioned'
                    ? 'color-mix(in srgb, #0078d4 10%, var(--bg-surface))'
                    : 'color-mix(in srgb, #e8b000 10%, var(--bg-surface))',
                  border: `1px solid ${bd.recommendation === 'standard' ? '#107c10' : bd.recommendation === 'provisioned' ? '#0078d4' : '#e8b000'}`,
                }}
              >
                <p className="font-semibold mb-0.5" style={{ color: bd.recommendation === 'standard' ? '#107c10' : bd.recommendation === 'provisioned' ? '#0078d4' : '#e8b000' }}>
                  {bd.recommendation === 'standard' ? 'Recommendation: Standard PAYG' : bd.recommendation === 'provisioned' ? 'Recommendation: Provisioned (PTU)' : 'Borderline — consider your needs'}
                </p>
                <p style={{ color: 'var(--text-secondary)' }}>{bd.recommendationReason}</p>
              </div>
            </div>
          )}

          {data && (
            <FormulaDisclosure lines={formulaLines} fetchedAt={data.prices_last_updated} />
          )}

          <a href="https://azure.microsoft.com/en-gb/pricing/details/cognitive-services/openai-service/" target="_blank" rel="noopener noreferrer" className="flex items-center gap-1.5 text-[10px] font-mono mt-2" style={{ color: 'var(--text-tertiary)' }}>
            <span className="material-symbols-outlined text-[13px]">open_in_new</span>
            Verify on official Azure pricing page
          </a>
        </div>
      </div>

      <MobileCostBar amount={visibleTotal || null} label="Azure OpenAI / month" targetId="openai-result" />
    </div>
  )
}
